import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, Check, X, ExternalLink } from "lucide-react";
import { api } from "../../lib/api";

const ACTION_BADGE = {
  accept_all: ["ACEPTA TODO", "bg-emerald-100 text-emerald-700"],
  reject_all: ["RECHAZA", "bg-red-100 text-red-700"],
  custom: ["PERSONALIZADO", "bg-amber-100 text-amber-700"],
};

const Bool = ({ v }) => v
  ? <Check size={14} className="text-emerald-600 mx-auto" data-testid="consent-bool-yes" />
  : <X size={14} className="text-slate-300 mx-auto" data-testid="consent-bool-no" />;

export default function ConsentsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/content/consents").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const count = (a) => items.filter((i) => i.action === a).length;
  const stats = [
    ["Total", items.length, "text-[#0B1220]"],
    ["Aceptan todo", count("accept_all"), "text-emerald-600"],
    ["Rechazan", count("reject_all"), "text-red-600"],
    ["Personalizado", count("custom"), "text-amber-600"],
  ];

  return (
    <div data-testid="consents-page">
      <div className="flex items-center gap-2 mb-1">
        <Cookie size={18} className="text-[#5698C7]" strokeWidth={1.5} />
        <h1 className="font-display text-xl font-light">Consentimientos GDPR</h1>
      </div>
      <p className="font-mono-tech text-[10px] tracking-widest text-slate-400 mb-2">
        REGISTRO AUDITABLE DE CONSENTIMIENTOS DE COOKIES · IP ANONIMIZADA · SOLO LECTURA
      </p>
      <Link to="/legal/politica-de-cookies" target="_blank" className="inline-flex items-center gap-1 text-xs text-[#5698C7] hover:text-[#0B1220] transition-colors duration-200 mb-6" data-testid="consents-policy-link">
        <ExternalLink size={12} /> Documento legal: Política de Cookies
      </Link>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {stats.map(([label, n, color]) => (
          <div key={label} className="hud-border bg-white p-4" data-testid={`consents-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className={`font-display text-2xl font-light ${color}`}>{n}</div>
            <div className="font-mono-tech text-[10px] tracking-widest text-slate-400">{label.toUpperCase()}</div>
          </div>
        ))}
      </div>

      <div className="hud-border bg-white overflow-x-auto">
        <table className="w-full text-xs" data-testid="consents-table">
          <thead>
            <tr className="border-b border-slate-200 font-mono-tech text-[10px] tracking-widest text-slate-400 text-left">
              <th className="px-4 py-3">FECHA</th>
              <th className="px-4 py-3">ACCIÓN</th>
              <th className="px-4 py-3 text-center">ANALÍTICAS</th>
              <th className="px-4 py-3 text-center">MARKETING</th>
              <th className="px-4 py-3">DOC · VERSIÓN</th>
              <th className="px-4 py-3">IP</th>
              <th className="px-4 py-3">ID CONSENTIMIENTO</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Cargando...</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400" data-testid="consents-empty">Sin consentimientos registrados todavía.</td></tr>
            )}
            {items.map((c) => {
              const [label, cls] = ACTION_BADGE[c.action] || ["—", "bg-slate-100 text-slate-500"];
              return (
                <tr key={c._id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors duration-150" data-testid={`consent-row-${c._id}`}>
                  <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{new Date(c.created_at).toLocaleString("es-ES")}</td>
                  <td className="px-4 py-2.5"><span className={`font-mono-tech text-[9px] tracking-widest px-2 py-1 rounded ${cls}`}>{label}</span></td>
                  <td className="px-4 py-2.5"><Bool v={c.analytics} /></td>
                  <td className="px-4 py-2.5"><Bool v={c.marketing} /></td>
                  <td className="px-4 py-2.5 font-mono-tech text-slate-500">cookies · v{c.doc_version ?? "—"}</td>
                  <td className="px-4 py-2.5 font-mono-tech text-slate-400">{c.ip || "—"}</td>
                  <td className="px-4 py-2.5 font-mono-tech text-[10px] text-slate-400" title={c.consent_id}>{(c.consent_id || "").slice(0, 8)}…</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
